import { Component, Inject, OnDestroy, OnInit } from '@angular/core';
import { Observable, Subject } from "rxjs";
import { FormBuilder, FormControl, FormGroup, UntypedFormControl, Validators } from "@angular/forms";
import { PaymentLinkInterface } from "@modules/administration/pricing-plans/interfaces/payment-link.interface";
import { AccountTypeDto } from "@core/services/account-types/account-type-dto";
import {
  MAT_LEGACY_DIALOG_DATA as MAT_DIALOG_DATA,
  MatLegacyDialogRef as MatDialogRef
} from "@angular/material/legacy-dialog";
import {
  PaymentLinkDialogDataInterface
} from "@modules/administration/pricing-plans/interfaces/payment-link-dialog-data.interface";
import { PaymentLinkState } from "@modules/administration/pricing-plans/payment-link-dialog/payment-link-dialog.state";
import { MatLegacySnackBar as MatSnackBar } from "@angular/material/legacy-snack-bar";
import { AppConstants } from "@core/constants";
import moment from 'moment';

const DEFAULT_ACCOUNT_TYPE_NAME = "Commercial User";

@Component({
  selector: 'app-payment-link-dialog-v2',
  templateUrl: './payment-link-dialog-v2.component.html',
  styleUrls: ['./payment-link-dialog-v2.component.scss'],
  providers: [PaymentLinkState]
})
export class PaymentLinkDialogV2Component implements OnInit, OnDestroy {
  private _destroy$: Subject<void> = new Subject<void>();

  public loading$: Observable<boolean>;

  public paymentLinkForm!: FormGroup;
  public paymentLink: PaymentLinkInterface;
  public onboardingLink: string | null = null;
  public accountTypes: AccountTypeDto[];
  public accountTypeOptions: AccountTypeDto[] = [];
  public discountTypes: any;

  constructor(
    @Inject(MAT_DIALOG_DATA) public data: PaymentLinkDialogDataInterface,
    public dialogRef: MatDialogRef<PaymentLinkDialogV2Component>,
    private fb: FormBuilder,
    private state: PaymentLinkState,
    private readonly snackBar: MatSnackBar,
  ) { }

  public ngOnInit(): void {
    this.loading$ = this.state.loading$;
    this.state.onboardingLink$.subscribe(onboardingLink => {
      this.onboardingLink = onboardingLink;
    })
    this.state.accountTypes$.subscribe(accountTypes => this.accountTypes = accountTypes);
    this.initForm();
    this.state.fetchAccountTypes().subscribe(() => {
      const accountTypeIds = this.data.plan.account_types || [];
      if (accountTypeIds.length === 0) {
        this.accountTypeOptions = this.accountTypes;
      } else {
        this.accountTypeOptions = this.accountTypes.filter(a => accountTypeIds.includes(a.id!));
      }
      this.initFormValues();
      this.initOptionAndValue();
    });
  }

  public initOptionAndValue(): void {
    this.discountTypes = AppConstants.DISCOUNT_TYPES;
  }

  ngOnDestroy(): void {
    this._destroy$.next();
    this._destroy$.complete();
  }

  public cancel() { this.dialogRef.close([false, undefined]); }

  public sendEmail() {
    const extraData: any = {
      discount_value: this.paymentLinkForm.get("discount_value")?.value,
      discount_type: this.paymentLinkForm.get("discount_type")?.value,
      link_expiration_date: this.paymentLinkForm.get("link_expiration_date")?.value,
      email: this.paymentLinkForm.get("email")?.value,
    };
    this.state.emailPaymentUrlV2(this.data.plan.id!, this.paymentLink, extraData)
      .subscribe(() => {
        this.snackBar.open("Email successfuly sent", 'OK', AppConstants.TOAST_CONFIG.SUCCESS);
        this.dialogRef.close([true, undefined]);
      })
  }

  public generatePaymentLink() {
    this.state.generatePaymentLink(this.data.plan.id!, this.paymentLinkForm.get("account_type")?.value);
  }

  public generateOnboardingLink() {
    const planStartDate: moment.Moment = this.paymentLinkForm.get('plan_start_date')?.value;

    const linkData = {...this.paymentLinkForm.value, plan: this.data.plan.id! };
    if (planStartDate) {
      linkData.plan_start_date_local = this.toLocalMidnightUtc(planStartDate.toISOString());
    }

    linkData.discount_type = linkData.discount_type === 1 ? 'percent' : 'total';
    if (!linkData.discount_value) {
      linkData.discount_type = null;
      linkData.apply_for_days = null;
    }
    this.state.generateOnboardingLink(linkData);
  }

  public canGenerateLink() {
    return this.paymentLinkForm.get("account_type")?.value != null;
  }

  public showAccountType(): boolean {
    return (this.data.plan.account_types || []).length !== 1;
  }

  private initForm() {
    this.paymentLinkForm = this.fb.group({
      email: new FormControl<string>("", [Validators.email, Validators.required]),
      account_type: new FormControl<number | undefined>(undefined),
      discount_type: new UntypedFormControl(1),
      discount_value: new UntypedFormControl(null),
      apply_for_days: new UntypedFormControl(null),
      link_expiration_date: new UntypedFormControl(moment().add(2, 'weeks').toDate()),
      plan_start_date: new UntypedFormControl(null),
    });
  }

  private initFormValues() {
    const accountTypes = this.data.plan?.account_types || [];
    if (accountTypes.length === 0) {
      this.paymentLinkForm.patchValue({
        account_type: this.defaultAccountType().id
      });
    } else if (accountTypes.length === 1) {
      this.paymentLinkForm.patchValue({
        account_type: accountTypes[0]
      });
    }
  }

  private defaultAccountType(): AccountTypeDto {
    const commercialUser = this.accountTypes.find(a => a.name === DEFAULT_ACCOUNT_TYPE_NAME);
    return commercialUser || this.accountTypes[0];
  }

  toLocalMidnightUtc(dateString: string): string | null {
    if (!dateString) return null;
    
    const utcMoment = moment.utc(dateString);
    const localMoment = utcMoment.local();
    const localMidnight = localMoment.clone().startOf('day');

    return localMidnight.format().slice(0, 19);
  }
}
